import { useState, useEffect, useCallback, useRef } from "react"
import { Company } from "~/types/company"

interface UseCompanySearchConfig {
  domainIds: number[]
  enabled?: boolean
}

export const useCompanySearch = (config: UseCompanySearchConfig) => {
  const { domainIds, enabled = true } = config

  const [companies, setCompanies] = useState<Company[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  // Track previous domainIds by value (not reference) to prevent infinite loops
  const previousDomainIdsKeyRef = useRef<string>("")

  const fetchCompanies = useCallback(async () => {
    if (!enabled || domainIds.length === 0) return

    setIsLoading(true)
    try {
      const csrfToken = (document.querySelector("meta[name='csrf-token']") as HTMLMetaElement)?.content || ""
      const response = await fetch("/search/companies", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ domain_ids: domainIds }),
      })

      if (!response.ok) {
        throw new Error("Failed to fetch companies")
      }

      const data = await response.json()
      setCompanies(data.companies || [])
    } catch (error) {
      console.error("Error fetching companies:", error)
      if (typeof (window as any).App !== "undefined") {
        ;(window as any).App.displayFlash("error", "Failed to load companies")
      }
    } finally {
      setIsLoading(false)
    }
  }, [domainIds, enabled])

  useEffect(() => {
    // Create a stable string key from domainIds for comparison
    // Sort to ensure consistent ordering regardless of input order
    const currentDomainIdsKey = JSON.stringify([...domainIds].sort((a, b) => a - b))
    const domainIdsChanged = currentDomainIdsKey !== previousDomainIdsKeyRef.current

    // Only fetch if domainIds actually changed (by value, not reference)
    if (enabled && domainIds.length > 0 && domainIdsChanged) {
      previousDomainIdsKeyRef.current = currentDomainIdsKey
      fetchCompanies()
    }
    // fetchCompanies is intentionally omitted from deps - we only want to refetch when domainIds/enabled change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, domainIds.join(",")])

  const handleSelect = useCallback((ids: number[]) => {
    setSelectedIds((prev) => {
      const newIds = [...prev]
      ids.forEach((id) => {
        const index = newIds.indexOf(id)
        if (index > -1) {
          newIds.splice(index, 1)
        } else {
          newIds.push(id)
        }
      })
      return newIds
    })
  }, [])

  return {
    companies,
    setCompanies,
    isLoading,
    selectedIds,
    handleSelect,
    fetchCompanies,
  }
}
